
import { db, collection, doc, getDocs, setDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy, limit, where } from "../../firebaseClient";
import { stripUndefinedDeep } from "../utils/firestoreClean";
import { ComplianceCard, CoOwnerEntry, ApprovalHistoryEntry } from "../types/compliance";
import { getDeptNameTW, inferRegulatorTW } from "../constants/complianceMappingTW";
import { COL_COMPLIANCE_CARDS, COL_JOB_LOGS } from "../constants/collections";
import { createOrResetApprovalFlow } from "./approvalFlowService";

const CARD_COLLECTION = COL_COMPLIANCE_CARDS;
const JOB_LOG_COLLECTION = COL_JOB_LOGS;

export async function createComplianceCard(data: Partial<ComplianceCard>): Promise<string> {
  const docRef = collection(db, CARD_COLLECTION).doc();
  
  // Default Structure
  const payload: ComplianceCard = {
    id: docRef.id,
    type: data.type || "intel",
    title: data.title || "未命名",
    regulatorName: data.regulatorName || null,
    ownerDeptName: data.ownerDeptName || null,
    riskLevel: data.riskLevel || "LOW",
    riskScore: data.riskScore || 0,
    status: data.status || "DRAFT",
    revision: 0,
    approvalHistory: [],
    
    sectionA: {
        title: data.sectionA?.title || data.title || "",
        publishDate: data.sectionA?.publishDate || null,
        announcementUrl: data.sectionA?.announcementUrl || null,
        sourceUrl: data.sectionA?.sourceUrl || null,
        summary: data.sectionA?.summary || "",
        attachments: data.sectionA?.attachments || [],
        regulatorName: data.sectionA?.regulatorName || data.regulatorName || null
    },
    sectionB: {
        ownerDeptCode: data.sectionB?.ownerDeptCode || null,
        ownerDeptName: data.sectionB?.ownerDeptName || null,
        ownerAssigneeName: null,
        ownerContent: "",
        attachments: []
    },
    sectionC: {
        coowners: data.sectionC?.coowners && data.sectionC.coowners.length > 0 ? data.sectionC.coowners : [
            { deptCode: "LG", deptName: "法務部", attachments: [], comment: "" } // Default Legal Co-assist
        ]
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(docRef, stripUndefinedDeep(payload), { merge: true });
  return docRef.id;
}

export async function updateComplianceCard(id: string, data: Partial<ComplianceCard>): Promise<void> {
  const docRef = doc(db, CARD_COLLECTION, id);
  const payload = {
    ...data,
    updatedAt: serverTimestamp()
  };
  // IMPORTANT: Sanitize before writing to prevent undefined errors
  await updateDoc(docRef, stripUndefinedDeep(payload));
}

export async function deleteComplianceCard(id: string) {
    await deleteDoc(doc(db, CARD_COLLECTION, id));
}

/**
 * Submit for Approval flow - UPDATED for Unified Flow Integration
 */
export async function submitCardForApproval(
    card: ComplianceCard, 
    actorEmail: string,
    actorUid: string,
    actorName: string
): Promise<void> {
    const newRevision = (card.revision || 0) + 1;
    
    const historyEntry: ApprovalHistoryEntry = {
        at: new Date().toISOString(),
        by: actorEmail,
        action: 'SUBMIT',
        comment: '送出呈核 (啟動流程)',
        revision: newRevision
    };
    
    // 1. Update Card Status
    await updateComplianceCard(card.id, {
        status: 'IN_REVIEW',
        revision: newRevision,
        approvalHistory: [...(card.approvalHistory || []), historyEntry] as any
    });

    // 2. Initialize Unified Approval Flow
    // Flow ID convention: COMPLIANCE_PROJECT_{cardId}
    const flowModule = "COMPLIANCE_PROJECT";
    
    await createOrResetApprovalFlow(
        flowModule,
        card.id,
        actorUid,
        card.sectionB?.ownerDeptName || "主責單位",
        actorName
    );
}

/**
 * Review Action (Legacy/Manual Override)
 * Used if not using ApprovalFlowPanel
 */
export async function reviewCardAction(
    card: ComplianceCard, 
    action: 'APPROVE' | 'REJECT', 
    actorEmail: string, 
    comment: string
): Promise<void> {
    const historyEntry = {
        at: new Date().toISOString(),
        by: actorEmail,
        action: action,
        comment: comment,
        revision: card.revision
    };
    
    let newStatus = card.status;
    if (action === 'REJECT') {
        newStatus = 'REJECTED';
    } else if (action === 'APPROVE') {
        newStatus = 'APPROVED';
    }

    await updateComplianceCard(card.id, {
        status: newStatus,
        approvalHistory: [...(card.approvalHistory || []), historyEntry] as any
    });
}

/**
 * One-Click Fix: Backfill missing fields without overwriting user data
 */
export async function runAuthorityBackfill() {
    console.log("[Backfill] Starting safe backfill...");
    const snap = await getDocs(collection(db, CARD_COLLECTION));
    const batch = db.batch();
    
    let fixedCount = 0;
    let ops = 0;

    for (const d of snap.docs) {
        const data = d.data() as any;
        const updates: any = {};
        
        // 0. Fix Type
        if (!data.type) {
            if (data.sectionA?.sourceUrl || data.sourceUrl) {
                updates['type'] = 'intel';
            } else {
                updates['type'] = 'intel';
            }
        }

        // 0.5 Fix Status
        if (!data.status) {
            updates['status'] = 'DRAFT';
        }

        // 1. Fix Dept Name
        let ownerDeptName = data.ownerDeptName || data.sectionB?.ownerDeptName;
        const ownerDeptCode = data.sectionB?.ownerDeptCode || data.ownerDept; 

        if (!ownerDeptName && ownerDeptCode) {
            ownerDeptName = getDeptNameTW(ownerDeptCode);
            updates['sectionB.ownerDeptName'] = ownerDeptName;
            updates['sectionB.ownerDeptCode'] = ownerDeptCode;
            updates['ownerDeptName'] = ownerDeptName;
        } else if (!ownerDeptName && !ownerDeptCode) {
            updates['sectionB.ownerDeptName'] = null;
            updates['ownerDeptName'] = null;
        }

        // 2. Fix Regulator
        let regulatorName = data.regulatorName || data.sectionA?.regulatorName;
        if (!regulatorName || regulatorName === "待補齊") {
            const inference = inferRegulatorTW(data.title || data.sectionA?.title || "", data.sectionA?.sourceUrl);
            if (inference.confidence > 0.5) {
                regulatorName = inference.name;
                updates['sectionA.regulatorName'] = regulatorName;
                updates['sectionA.regulatorId'] = inference.id;
                updates['regulatorName'] = regulatorName;
            } else {
                if (!data.regulatorName) updates['regulatorName'] = "待補齊";
            }
        }

        // 3. Fix Risk Score
        if (data.riskScore === undefined) {
             const level = data.riskLevel || "LOW";
             let baseScore = level === "HIGH" ? 85 : level === "MEDIUM" ? 60 : 30;
             updates['riskScore'] = baseScore;
        }

        // 4. Ensure Section C
        if (!data.sectionC || !data.sectionC.coowners || data.sectionC.coowners.length === 0) {
            updates['sectionC'] = {
                coowners: [{ deptCode: "LG", deptName: "法務部", attachments: [], comment: "" }]
            };
        }

        if (Object.keys(updates).length > 0) {
            batch.update(d.ref, stripUndefinedDeep(updates));
            fixedCount++;
            ops++;
        }
        
        if (ops >= 400) {
            await batch.commit();
            ops = 0;
        }
    }

    if (ops > 0) await batch.commit();
    return { total: snap.size, fixed: fixedCount };
}

export async function listComplianceCards(type?: string, isHistory = false): Promise<ComplianceCard[]> {
    const q = query(collection(db, CARD_COLLECTION), orderBy("updatedAt", "desc"), limit(200));
    
    const snap = await getDocs(q);
    let cards = snap.docs.map(d => ({ id: d.id, ...d.data() } as ComplianceCard));

    if (isHistory) {
        cards = cards.filter(c => ['APPROVED', 'ARCHIVED', 'COMPLETED', 'REJECTED', 'RETURNED'].includes(c.status));
    } else {
        cards = cards.filter(c => !['APPROVED', 'ARCHIVED', 'COMPLETED', 'REJECTED', 'RETURNED'].includes(c.status));
        if (type) {
            cards = cards.filter(c => c.type === type);
        }
    }
    
    return cards;
}

export async function getLatestCrawlerLog() {
    const q = query(collection(db, JOB_LOG_COLLECTION), where("jobKey", "==", "crawler_0830"), orderBy("startedAt", "desc"), limit(1));
    const snap = await getDocs(q);
    return snap.empty ? null : snap.docs[0].data();
}
